/**
 * Pure rules shared by the session bottom sheet, the Dashboard cards, the
 * date-change modal and the training log form (plan sections 3a–3c and the
 * decisions in section 6). Kept free of React / Firebase so they are unit
 * testable and behave identically on every surface.
 */
import { Session } from "../types/post";

/** Minimal session shape the rules need. */
export type SessionRuleInput = Pick<
  Session,
  | "status"
  | "linkedTrainingLogID"
  | "coachIssued"
  | "coachID"
  | "programLinkage"
  | "startTime"
  | "endTime"
  | "trainingTypeID"
  | "sessionTypeID"
  | "name"
>;

/**
 * Coach-assigned sessions are rejected (with a reason the coach can see)
 * instead of deleted. Mirrors `Session.tsx` / Schedule.
 */
export const isCoachAssignedSession = (
  session: SessionRuleInput | null | undefined,
): boolean =>
  Boolean(
    session &&
      (session.coachIssued || session.coachID || session.programLinkage),
  );

export interface DateChangeOptions {
  /**
   * Whether a wearable activity (HR / GPS) is attached to the session via its
   * training log. Completed sessions with wearable data cannot move.
   */
  hasWearableActivity?: boolean;
}

/**
 * Decision 2: planned sessions can always move; completed sessions only when
 * they are unlinked (no training log) and have no wearable activity attached.
 * Proposed sessions must be accepted first, executing sessions are live, and
 * rejected sessions are gone from the calendar.
 * Decision 3: coach-assigned program sessions may move like any other.
 */
export const canChangeSessionDate = (
  session: SessionRuleInput | null | undefined,
  options: DateChangeOptions = {},
): boolean => {
  if (!session) return false;
  switch (session.status) {
    case "planned":
      return true;
    case "complete":
      return !session.linkedTrainingLogID && !options.hasWearableActivity;
    default:
      return false;
  }
};

/** Why a session cannot move; `null` when it can. Used for the disabled hint. */
export const getDateChangeBlockedReason = (
  session: SessionRuleInput | null | undefined,
  options: DateChangeOptions = {},
): string | null => {
  if (!session) return "Session not found";
  if (canChangeSessionDate(session, options)) return null;
  switch (session.status) {
    case "proposed":
      return "Accept the session before changing its date";
    case "executing":
      return "A session in progress cannot be moved";
    case "rejected":
      return "Rejected sessions cannot be moved";
    case "complete":
      if (session.linkedTrainingLogID) {
        return "Unlink the session from its training log to move it";
      }
      return "Sessions with wearable activity data cannot be moved";
    default:
      return "This session cannot be moved";
  }
};

export interface SessionSheetOptions extends DateChangeOptions {
  /** False when viewing another athlete's dashboard (read-only). */
  canEdit?: boolean;
}

export interface SessionSheetActions {
  /** "Open session" (`/session/:id`) — always available. */
  open: true;
  /** "Link to training log" — completed and not yet linked. */
  link: boolean;
  /** "Change date" per `canChangeSessionDate`. */
  changeDate: boolean;
  /** "Delete session" — the athlete's own session. */
  delete: boolean;
  /** "Reject session" with a reason — coach-issued instead of delete. */
  reject: boolean;
}

/**
 * Which footer actions the session bottom sheet shows for a session
 * (plan 3b/3c). Coach-issued sessions get Reject instead of Delete so the
 * coach keeps a record of the athlete declining.
 */
export const getSessionSheetActions = (
  session: SessionRuleInput | null | undefined,
  options: SessionSheetOptions = {},
): SessionSheetActions => {
  const canEdit = options.canEdit ?? true;
  if (!session || !canEdit) {
    return {
      open: true,
      link: false,
      changeDate: false,
      delete: false,
      reject: false,
    };
  }
  const coach = isCoachAssignedSession(session);
  // A running session is only cancelled from the live page.
  const executing = session.status === "executing";
  return {
    open: true,
    link: session.status === "complete" && !session.linkedTrainingLogID,
    changeDate: canChangeSessionDate(session, options),
    delete: !coach && !executing,
    reject: coach && !executing,
  };
};

/** Whole minutes between a session's start and end, or 0 when unknown. */
export const getSessionDurationMinutes = (
  session: Pick<Session, "startTime" | "endTime"> | null | undefined,
): number => {
  if (!session?.startTime || !session?.endTime) return 0;
  const start = new Date(session.startTime).getTime();
  const end = new Date(session.endTime).getTime();
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) {
    return 0;
  }
  return Math.max(1, Math.round((end - start) / 60000));
};

/**
 * Default training-log duration when N sessions are linked: the sum of each
 * session's `endTime - startTime` (decision 6). Sessions without timestamps
 * contribute 0. Returns 0 when nothing can be derived so the caller can leave
 * the field empty; otherwise at least 1 minute.
 */
export const sumSessionDurationMinutes = (
  sessions: (Pick<Session, "startTime" | "endTime"> | null | undefined)[],
): number => {
  const total = sessions.reduce(
    (sum, session) => sum + getSessionDurationMinutes(session),
    0,
  );
  return total > 0 ? Math.max(1, total) : 0;
};

/**
 * Whether the athlete must pick a primary training type: only when more than
 * one session is linked (decision 6 — no silent default).
 */
export const requiresPrimarySelection = (linkedSessionIDs: string[]): boolean =>
  linkedSessionIDs.length > 1;

/**
 * Order linked session ids so the chosen primary is element 0. Returns `null`
 * when a primary is required but not chosen (or not among the linked ids),
 * so the form can block submission. A single session is its own primary.
 */
export const orderLinkedSessionIDs = (
  linkedSessionIDs: string[],
  primarySessionID: string | null | undefined,
): string[] | null => {
  const unique = Array.from(new Set(linkedSessionIDs.filter(Boolean)));
  if (unique.length <= 1) return unique;
  if (!primarySessionID || !unique.includes(primarySessionID)) return null;
  return [primarySessionID, ...unique.filter((id) => id !== primarySessionID)];
};

export interface PrimaryTypeOption {
  /** The session id that becomes `linkedSessionIDs[0]` when chosen. */
  sessionID: string;
  /** Resolved training type id, when the session has one. */
  trainingTypeID: string | null;
  /** Label shown in the picker: the type name, falling back to the session name. */
  label: string;
}

/**
 * Options for the "Primary training type" picker: one per linked session,
 * labelled with that session's training type (or its name when it has none)
 * so the athlete picks a type and, implicitly, the primary session.
 */
export const getPrimaryTypeOptions = (
  sessions: (SessionRuleInput & { id: string })[],
  resolveTypeName: (session: SessionRuleInput) => string | null | undefined,
): PrimaryTypeOption[] =>
  sessions.map((session) => {
    const typeName = resolveTypeName(session)?.trim() || null;
    const sessionName = session.name?.trim() || null;
    return {
      sessionID: session.id,
      trainingTypeID: session.trainingTypeID?.trim() || null,
      label: typeName || sessionName || "Session (no training type)",
    };
  });

/** Local-time YYYY-MM-DD, avoiding the UTC shift of `toISOString()`. */
export const toLocalDateString = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};
